/**
 * Zod schemas of the DATA props of the shared extranet cards — what a domain
 * `register.ts` passes as `propsSchema` when it registers a usage of a shared
 * card (the bound config props — `componentId`, `title`, `fields`, `icon` —
 * and the injected `_layoutOverrides` are deliberately absent: rules target
 * stable keys through `_layoutOverrides`, never the config).
 *
 * Each schema is checked against its TypeScript props with `satisfies`, so a
 * props change fails the build until the schema follows. Sample
 * `*_PREVIEW_PROPS` feed the admin config page's preview.
 */

import { z } from 'zod';

import type { ClaimsTableCardProps } from './ClaimsTableCard';
import type { DocumentsByCategoryCardProps } from './DocumentsByCategoryCard';
import type { EntityInfoCardProps } from './EntityInfoCard';
import type { GuaranteesTableCardProps } from './GuaranteesTableCard';
import type { PiecesCardProps } from './PiecesCard';
import type { PieceUploadOrigin } from './pieceItems';

// ---------------------------------------------------------------------------
// Data-prop types of a registered usage
// ---------------------------------------------------------------------------

/** Props a page passes to a registered usage (config props are bound). */
export type EntityInfoCardDataProps = Pick<EntityInfoCardProps, 'values' | 'loading' | 'error'>;
export type DocumentsCardDataProps = Pick<
  DocumentsByCategoryCardProps,
  'documents' | 'loading' | 'error'
>;
export type PiecesCardDataProps = Pick<PiecesCardProps, 'pieces' | 'loading' | 'error'>;
export type ClaimsTableCardDataProps = Pick<ClaimsTableCardProps, 'rows' | 'loading' | 'error'>;
export type GuaranteesTableCardDataProps = Pick<
  GuaranteesTableCardProps,
  'rows' | 'loading' | 'error'
>;

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

const loadingSchema = z.boolean().optional();
const errorSchema = z.string().nullable().optional();

export const entityInfoCardDataSchema = z.object({
  values: z.record(z.string(), z.union([z.string(), z.number(), z.null()])).nullable(),
  loading: loadingSchema,
  error: errorSchema,
}) satisfies z.ZodType<EntityInfoCardDataProps>;

export const documentItemSchema = z.object({
  id: z.string(),
  categorie: z.string().nullable(),
  nom: z.string().nullable(),
  date: z.string().nullable(),
});

export const documentsCardDataSchema = z.object({
  documents: z.array(documentItemSchema),
  loading: loadingSchema,
  error: errorSchema,
}) satisfies z.ZodType<DocumentsCardDataProps>;

/** Upload ids of a contract / claim piece (fiches 22975 / 22976). */
export const pieceUploadTargetSchema = z.object({
  origin: z.enum(['contrat', 'sinistre']),
  idPmd: z.number(),
  idTab: z.number(),
  parentId: z.number(),
  cdDs: z.string(),
});

export const pieceItemSchema = z.object({
  id: z.string(),
  libelle: z.string().nullable(),
  dateDemande: z.string().nullable(),
  statut: z.string().nullable(),
  upload: pieceUploadTargetSchema.nullable().optional(),
});

export const piecesCardDataSchema = z.object({
  pieces: z.array(pieceItemSchema),
  loading: loadingSchema,
  error: errorSchema,
}) satisfies z.ZodType<PiecesCardDataProps>;

export const claimTableRowSchema = z.object({
  id: z.string(),
  reference: z.string().nullable(),
  circonstances: z.string().nullable(),
  dateSurvenance: z.union([z.number(), z.string()]).nullable(),
  coutAssureur: z.number().nullable(),
  to: z.string().nullable().optional(),
});

export const claimsTableCardDataSchema = z.object({
  rows: z.array(claimTableRowSchema),
  loading: loadingSchema,
  error: errorSchema,
}) satisfies z.ZodType<ClaimsTableCardDataProps>;

export const guaranteeTableRowSchema = z.object({
  id: z.string(),
  libelle: z.string().nullable(),
  plafond: z.number().nullable(),
  franchise: z.number().nullable(),
});

export const guaranteesTableCardDataSchema = z.object({
  rows: z.array(guaranteeTableRowSchema),
  loading: loadingSchema,
  error: errorSchema,
}) satisfies z.ZodType<GuaranteesTableCardDataProps>;

// ---------------------------------------------------------------------------
// Default prop labels (admin UI)
// ---------------------------------------------------------------------------

export const SHARED_CARD_PROP_LABELS = {
  values: 'Valeurs',
  documents: 'Documents',
  pieces: 'Pièces',
  rows: 'Lignes',
  loading: 'Chargement en cours',
  error: "Message d'erreur",
} as const;

// ---------------------------------------------------------------------------
// Preview samples (admin config page)
// ---------------------------------------------------------------------------

export const DOCUMENTS_PREVIEW_PROPS: DocumentsCardDataProps = {
  documents: [
    { id: '1', categorie: 'Conditions', nom: 'Conditions particulières', date: '07/09/2026' },
    { id: '2', categorie: 'Conditions', nom: 'Conditions générales', date: '07/09/2026' },
    { id: '3', categorie: 'Attestations', nom: "Attestation d'assurance", date: '08/09/2026' },
  ],
};

export const PIECES_PREVIEW_PROPS: PiecesCardDataProps = {
  pieces: [
    { id: '1', libelle: 'Relevé d’information', dateDemande: '30/09/2026', statut: 'EN ATTENTE' },
    { id: '2', libelle: 'Carte grise', dateDemande: '30/09/2026', statut: 'EN ATTENTE' },
  ],
};

/**
 * Preview of a pieces usage with upload (contract / claim « Pièces » tab):
 * the sample pieces carry upload ids, so the admin preview shows the `depot`
 * zone (fiche 22975 / 22976 sample ids).
 */
export function piecesUploadPreviewProps(origin: PieceUploadOrigin): PiecesCardDataProps {
  const ids =
    origin === 'contrat'
      ? { idTab: 89829, parentId: 34965, cdDs: 'PMQP' }
      : { idTab: 127723, parentId: 127723, cdDs: 'PMQS' };
  return {
    pieces: PIECES_PREVIEW_PROPS.pieces.map((piece, index) => ({
      ...piece,
      upload: { origin, idPmd: 310062 + index, ...ids },
    })),
  };
}

export const CLAIMS_TABLE_PREVIEW_PROPS: ClaimsTableCardDataProps = {
  rows: [
    {
      id: '1',
      reference: 'SIN-2026-0042',
      circonstances: 'Bris de glace',
      dateSurvenance: 1787954400000,
      coutAssureur: 1800,
    },
  ],
};

export const GUARANTEES_TABLE_PREVIEW_PROPS: GuaranteesTableCardDataProps = {
  rows: [
    { id: 'RC', libelle: 'Responsabilité civile', plafond: 1_000_000, franchise: null },
    { id: 'BDG', libelle: 'Bris de glace', plafond: 2_500, franchise: 150 },
  ],
};
